import React, { useState, useRef, useEffect } from "react"
import { createPortal } from "react-dom"
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, X } from "lucide-react"

interface DatePickerProps {
  value: string
  onChange: (val: string) => void
  min?: string
  placeholder?: string
  className?: string
  placement?: "top" | "bottom" | "auto"
  disabled?: boolean
}

function parseDate(str?: string): Date | null {
  if (!str) return null
  const parts = str.split("-")
  if (parts.length === 3) {
    if (parts[0].length === 4) {
      // YYYY-MM-DD
      const y = Number(parts[0])
      const m = Number(parts[1]) - 1
      const d = Number(parts[2])
      const dt = new Date(y, m, d)
      if (!isNaN(dt.getTime())) return dt
    } else if (parts[2].length === 4) {
      // DD-MM-YYYY (reversed format fallback)
      const d = Number(parts[0])
      const m = Number(parts[1]) - 1
      const y = Number(parts[2])
      const dt = new Date(y, m, d)
      if (!isNaN(dt.getTime())) return dt
    }
  }
  const dt = new Date(str)
  return isNaN(dt.getTime()) ? null : dt
}

export function DatePicker({ value, onChange, min, placeholder = "Select date", className, placement = "auto", disabled = false }: DatePickerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const [currentDate, setCurrentDate] = useState(() => {
    if (value) {
      const d = parseDate(value)
      if (d) return d
    }
    if (min) {
      const d = parseDate(min)
      if (d) return d
    }
    return new Date()
  })
  
  const containerRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  // Calculate coordinates to render outside any scrollable/overflow-hidden containers
  const updatePosition = () => {
    if (!containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    const popoverWidth = 256
    const popoverHeight = 310

    let top = 0
    if (placement === "top") {
      top = Math.max(10, rect.top - popoverHeight - 6)
    } else if (placement === "bottom") {
      top = rect.bottom + 6
    } else {
      // "auto": if space below is less than popoverHeight + 60, position above
      if (rect.bottom + popoverHeight > window.innerHeight - 60 && rect.top - popoverHeight - 6 > 0) {
        top = Math.max(10, rect.top - popoverHeight - 6)
      } else {
        top = rect.bottom + 6
      }
    }

    // Horizontal placement:
    // If the trigger is near the right edge (like inside a filter drawer), anchor to right edge
    let left = rect.left
    if (left + popoverWidth > window.innerWidth - 12) {
      left = Math.max(12, rect.right - popoverWidth)
    }

    setPopoverPos({ top, left })
  }

  // Close calendar when clicking outside both container & portal popover
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        popoverRef.current &&
        !popoverRef.current.contains(target)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Reposition on scroll or resize while open
  useEffect(() => {
    if (!isOpen) return
    updatePosition()

    const handleScrollOrResize = () => {
      updatePosition()
    }

    window.addEventListener("scroll", handleScrollOrResize, true)
    window.addEventListener("resize", handleScrollOrResize)
    return () => {
      window.removeEventListener("scroll", handleScrollOrResize, true)
      window.removeEventListener("resize", handleScrollOrResize)
    }
  }, [isOpen])

  // Sync internal month/year state when value changes from outside
  useEffect(() => {
    if (value) {
      const d = parseDate(value)
      if (d) {
        setCurrentDate(d)
      }
    }
  }, [value])

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()

  // Helpers
  const getDaysInMonth = (y: number, m: number) => new Date(y, m + 1, 0).getDate()
  const getFirstDayOfMonth = (y: number, m: number) => new Date(y, m, 1).getDay()

  const daysInMonth = getDaysInMonth(year, month)
  const firstDay = getFirstDayOfMonth(year, month)

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ]

  const handlePrevMonth = (e: React.MouseEvent) => {
    e.stopPropagation()
    setCurrentDate(new Date(year, month - 1, 1))
  }

  const handleNextMonth = (e: React.MouseEvent) => {
    e.stopPropagation()
    setCurrentDate(new Date(year, month + 1, 1))
  }

  const handleDateClick = (day: number, e: React.MouseEvent) => {
    e.stopPropagation()
    const selectedDate = new Date(year, month, day)
    
    // Check min constraint
    if (min) {
      const minDate = parseDate(min)
      if (minDate) {
        minDate.setHours(0, 0, 0, 0)
        selectedDate.setHours(0, 0, 0, 0)
        if (selectedDate < minDate) return
      }
    }

    // Format selectedDate strictly as YYYY-MM-DD local time
    const yyyy = selectedDate.getFullYear()
    const mm = String(selectedDate.getMonth() + 1).padStart(2, '0')
    const dd = String(selectedDate.getDate()).padStart(2, '0')
    onChange(`${yyyy}-${mm}-${dd}`)
    setIsOpen(false)
  }

  // Generate grid cells
  const cells: React.ReactNode[] = []
  // Fill offset days
  for (let i = 0; i < firstDay; i++) {
    cells.push(<div key={`empty-${i}`} className="h-8 w-8" />)
  }
  // Fill days of the month
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  for (let day = 1; day <= daysInMonth; day++) {
    const dateOfCell = new Date(year, month, day)
    dateOfCell.setHours(0, 0, 0, 0)
    
    const cellDateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    const isSelected = value ? (
      value === cellDateStr || 
      (value.split("-").length === 3 && value.split("-")[2]?.length === 4 && value === `${String(day).padStart(2, '0')}-${String(month + 1).padStart(2, '0')}-${year}`)
    ) : false

    const isToday = today.getTime() === dateOfCell.getTime()
    
    let isDisabled = false
    if (min) {
      const minDate = parseDate(min)
      if (minDate) {
        minDate.setHours(0, 0, 0, 0)
        if (dateOfCell < minDate) {
          isDisabled = true
        }
      }
    }

    cells.push(
      <button
        key={`day-${day}`}
        type="button"
        disabled={isDisabled}
        onClick={(e) => handleDateClick(day, e)}
        className={`h-8 w-8 text-[12px] font-semibold rounded-lg flex items-center justify-center transition-all cursor-pointer ${
          isSelected
            ? "bg-[#be001e] text-white hover:bg-[#be001e]/90 font-bold shadow-sm"
            : isDisabled
            ? "text-slate-300 dark:text-zinc-700 cursor-not-allowed opacity-50"
            : isToday
            ? "text-[#be001e] hover:bg-[#be001e]/10 border border-[#be001e]/30 font-bold"
            : "text-slate-700 dark:text-zinc-300 hover:bg-slate-100 dark:hover:bg-zinc-800"
        }`}
      >
        {day}
      </button>
    )
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange("")
  }

  // Format value for display: e.g. "Jun 19, 2026"
  const getFormattedDisplay = () => {
    if (!value) return ""
    const d = parseDate(value)
    if (!d) return value
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
  }

  return (
    <div ref={containerRef} className="relative w-full text-left">
      <div 
        onClick={() => {
          if (disabled) return
          if (!isOpen) updatePosition()
          setIsOpen(!isOpen)
        }}
        className={`flex h-9 w-full items-center justify-between rounded-xl border border-slate-200 bg-slate-50/50 px-3 text-[12px] text-slate-800 outline-none transition-all shadow-sm ${
          disabled
            ? "opacity-60 cursor-not-allowed bg-slate-100"
            : "cursor-pointer hover:bg-white focus-within:border-[#be001e]/60 focus-within:bg-white focus-within:ring-2 focus-within:ring-[#be001e]/10"
        } ${className}`}
      >
        <span className={value ? "text-slate-800 font-semibold" : "text-slate-400"}>
          {getFormattedDisplay() || placeholder}
        </span>
        <div className="flex items-center gap-1.5 text-slate-400 dark:text-zinc-500">
          {value && !disabled && (
            <button 
              type="button" 
              onClick={handleClear} 
              className="p-0.5 rounded-full hover:bg-slate-100 dark:hover:bg-zinc-800 hover:text-slate-600 dark:hover:text-zinc-300 transition active:scale-95 cursor-pointer"
            >
              <X className="size-3.5 text-slate-400 hover:text-slate-600 dark:text-zinc-500 dark:hover:text-zinc-300" />
            </button>
          )}
          <CalendarIcon className="size-4 text-[#be001e] shrink-0" />
        </div>
      </div>

      {isOpen && typeof document !== "undefined" && createPortal(
        <div 
          ref={popoverRef}
          style={{
            position: "fixed",
            top: `${popoverPos.top}px`,
            left: `${popoverPos.left}px`,
          }}
          className="z-[99999] w-64 rounded-xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-3 shadow-2xl animate-in fade-in duration-100 select-none"
        >
          {/* Header */}
          <div className="flex items-center justify-between pb-2 mb-2 border-b border-slate-100 dark:border-zinc-800">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-zinc-800 text-slate-600 dark:text-zinc-400 transition cursor-pointer"
            >
              <ChevronLeft className="size-4" />
            </button>
            <span className="text-[13px] font-bold text-slate-800 dark:text-zinc-100">
              {monthNames[month]} {year}
            </span>
            <button
              type="button"
              onClick={handleNextMonth}
              className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-zinc-800 text-slate-600 dark:text-zinc-400 transition cursor-pointer"
            >
              <ChevronRight className="size-4" />
            </button>
          </div>

          {/* Weekdays */}
          <div className="grid grid-cols-7 gap-1 text-center mb-1">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
              <div key={day} className="text-[10px] font-extrabold uppercase text-slate-400 dark:text-zinc-500 h-6 flex items-center justify-center">
                {day}
              </div>
            ))}
          </div>

          {/* Days Grid */}
          <div className="grid grid-cols-7 gap-1">
            {cells}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
